import { Injectable, signal } from '@angular/core';

declare global {
    interface Window {
        grecaptcha?: {
            render: (container: HTMLElement, options: ReCaptchaV2Options) => number;
            getResponse: (widgetId?: number) => string;
            reset: (widgetId?: number) => void;
            ready: (callback: () => void) => void;
        };
        recaptchaOnloadCallback?: () => void;
    }
}

interface ReCaptchaV2Options {
    sitekey: string;
    theme?: 'light' | 'dark';
    size?: 'normal' | 'compact';
    callback?: (response: string) => void;
    'expired-callback'?: () => void;
    'error-callback'?: () => void;
}

@Injectable({
    providedIn: 'root'
})
export class RecaptchaService {
    private widgetId: number | null = null;
    private container: HTMLElement | null = null;
    private rendered = false;

    readonly isReady = signal(false);
    readonly isVerified = signal(false);

    constructor() {
        if (window.grecaptcha) {
            window.grecaptcha.ready(() => this.isReady.set(true));
        } else {
            window.recaptchaOnloadCallback = () => {
                this.isReady.set(true);
            };
        }
    }

    render(container: HTMLElement, siteKey: string): void {
        if (!window.grecaptcha || this.rendered) {
            return;
        }

        window.grecaptcha.ready(() => {
            if (this.rendered || !window.grecaptcha) {
                return;
            }
            this.container = container;
            this.widgetId = window.grecaptcha.render(container, {
                sitekey: siteKey,
                callback: () => this.isVerified.set(true),
                'expired-callback': () => this.isVerified.set(false),
                'error-callback': () => this.isVerified.set(false)
            });
            this.rendered = true;
            this.isReady.set(true);
        });
    }

    resetState(): void {
        this.widgetId = null;
        this.container = null;
        this.rendered = false;
        this.isVerified.set(false);
    }

    getResponse(): string {
        if (this.widgetId === null || !window.grecaptcha) {
            return '';
        }
        return window.grecaptcha.getResponse(this.widgetId);
    }

    reset(): void {
        if (this.widgetId !== null && window.grecaptcha) {
            window.grecaptcha.reset(this.widgetId);
        }
        this.isVerified.set(false);
    }
}
